import clsx from 'clsx';
import Image from 'next/image';
import dayjs from 'dayjs';
import Link from 'next/link';

import { Icon } from 'common/icon';

import {
  ICasino,
  ICasinoReview,
  IStreamer,
  IStreamerReview,
} from 'constants/types';
import { formatPluralWord } from 'utils/formats';

import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';

import styles from './styles.module.css';
import { Analytics } from 'constants/analytics';
import { StarRating } from 'components/star-rating';
import { Routes } from 'constants/routes';
import { getItemIdentifier } from 'utils/slug';
import { PanelReviewsCard } from './panel-card';
import { ReviewsCardFooter, RecommendStatus } from './footer';

export const ReviewsCard = ({
  isSlider = false,
  link,
  comment,
  isProfile,
  isPublicProfile,
  rating,
  handleLoginModal,
  type,
  variant = 'default',
}: {
  isSlider?: boolean;
  link?: string;
  isProfile?: boolean;
  isPublicProfile?: boolean;
  type?: 'casino' | 'streamer';
  variant?: 'default' | 'panel';
  comment: ICasinoReview | IStreamerReview;
  rating?: string;
  handleLoginModal?: () => void;
}) => {
  if (variant === 'panel') {
    return (
      <PanelReviewsCard
        link={link}
        comment={comment as ICasinoReview}
        isProfile={isProfile}
        isPublicProfile={isPublicProfile}
      />
    );
  }

  const imageUrl = isProfile
    ? comment?.data?.imageUrl
    : comment?.user?.imageUrl;
  const displayName =
    type === 'casino'
      ? (comment as ICasinoReview).data?.name
      : (comment as IStreamerReview).data?.username;
  if (isSlider) {
    return (
      <div className={styles.reviews__column}>
        <div
          className={clsx(
            styles.reviews__reviewSlider,
            styles.casino_review,
            styles.casino_review_recommended
          )}
        >
          <div className={styles.casino_review__top_line}>
            <RecommendStatus haveDescription recommend={comment.recommended} />

            {!isSlider && (
              <div className={styles.casino_review__date}>
                {dayjs(comment.createdAt).format('DD MMM, YYYY')}
              </div>
            )}
          </div>
          <div className={styles.casino_review_user_info}>
            <Link
              prefetch={false}
              id={
                isProfile
                  ? Analytics.reviews.visitCasinoPage
                  : Analytics.reviews.userPage
              }
              href={link ?? Routes.publicProfile(comment?.user?._id ?? '')}
              className={styles.casino_review__casino}
            >
              <div className={styles.casino_review__casino_info}>
                <div className={styles.casino_review__casino_photo}>
                  <LogoOverlayWrapper>
                    <LogoOverlay
                      width={50}
                      height={50}
                      alt={displayName}
                      image={comment?.data?.logoUrl}
                    />
                    {imageUrl && (
                      <Image
                        src={imageUrl}
                        width={50}
                        height={50}
                        alt="review-image"
                      />
                    )}
                  </LogoOverlayWrapper>
                </div>
                <div className={styles.casino_review__casino_about}>
                  <div className={styles.casino_review__casino_name}>
                    {isProfile
                      ? displayName
                      : (comment.user?.username ?? 'Username')}
                  </div>
                  {!isProfile && (
                    <div className={styles.casino_review__user_slider}>
                      <div className={styles.casino_review__user_inspections}>
                        {formatPluralWord({
                          number: comment?.user?.inspections_count ?? 0,
                          word: 'inspection',
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Link>
            {rating && (
              <StarRating
                ratingNumber={rating}
                starSize="15"
                color={'#caf413'}
              />
            )}
          </div>
          {getItemIdentifier(comment.item) && (
            <Link
              prefetch={false}
              href={
                type === 'casino'
                  ? Routes.casinoPage(getItemIdentifier(comment.item))
                  : Routes.streamer(getItemIdentifier(comment.item))
              }
              className={styles.review_casino_name}
            >
              {type === 'casino'
                ? (comment.item as ICasino).name
                : (comment.item as IStreamer).username}
            </Link>
          )}

          <div className={styles.casino_review__text__slider}>
            {comment.review}
          </div>
          <div className={styles.divider} />

          <ReviewsCardFooter
            handleLoginModal={handleLoginModal}
            isSlider={isSlider}
            reviewId={comment?._id}
            like={comment.likes ?? 0}
            dislike={comment.dislikes ?? 0}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reviews__column}>
      <div
        className={clsx(
          styles.reviews__review,
          styles.casino_review,
          styles.casino_review_recommended
        )}
      >
        <div className={styles.casino_review__top_line}>
          {comment?.rating ? (
            <StarRating
              ratingNumber={comment.rating?.toString()}
              starSize="15"
              color={'#caf413'}
            />
          ) : (
            <StarRating ratingNumber={'0'} starSize="15" color={'#caf413'} />
          )}

          <div className={styles.casino_review__date}>
            {dayjs(comment.createdAt).format('DD MMM, YYYY')}
          </div>
        </div>

        <Link
          prefetch={false}
          id={
            isProfile
              ? Analytics.reviews.visitCasinoPage
              : Analytics.reviews.userPage
          }
          href={link ?? Routes.publicProfile(comment?.user?._id ?? '')}
          className={styles.casino_review__casino}
        >
          <div className={styles.casino_review__casino_info}>
            <div className={styles.casino_review__casino_photo}>
              <LogoOverlayWrapper>
                <LogoOverlay
                  width={50}
                  height={50}
                  alt={displayName}
                  image={comment?.data?.logoUrl}
                />
                <Image
                  src={
                    isProfile
                      ? comment?.data?.imageUrl || ''
                      : comment?.user?.imageUrl || ''
                  }
                  width={50}
                  height={50}
                  alt="review-image"
                />
              </LogoOverlayWrapper>
            </div>
            <div className={styles.casino_review__casino_about}>
              <div className={styles.casino_review__casino_name}>
                {isProfile
                  ? displayName
                  : (comment.user?.username ?? 'Username')}
              </div>
              {!isProfile && (
                <div className={styles.casino_review__user_inspections}>
                  {formatPluralWord({
                    number: comment?.user?.inspections_count ?? 0,
                    word: 'inspection',
                  })}
                </div>
              )}
            </div>
          </div>
          <div className={styles.casino_review__casino_arrow}>
            <Icon.Arrow fillOpacity="0.3" />
          </div>
        </Link>

        <div className={styles.casino_review__text}>{comment.review}</div>

        <ReviewsCardFooter
          reviewId={comment?._id}
          like={comment.likes ?? 0}
          dislike={comment.dislikes ?? 0}
          isSlider={false}
        />
      </div>
    </div>
  );
};
